import {
  selectOrganizationId,
  useGetDemosQuery,
  useGetToursQuery,
} from '@carboncopyco/data';
import {
  DemoIndicatorColoredIcon,
  PlaybookIcon,
  RecordingIndicatorColoredIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { Dropdown, DropdownItem, Separator, TextBase } from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CreateAssetMenu } from '../../CreateAssetMenu';
import { CreateEntityModal } from '../../Demo';
import { CreatePlaybookModal } from '../../PlaybookModal';
import { useModalActions, usePermissionsContext } from '../../utils';
import { useWorkspacePageProps } from './WorkspacePageContext';

type WorkspacePageCreateMenuProps = {
  triggerElement: ReactElement;
  asChild?: boolean;
  location?: string;
};

export const WorkspacePageCreateMenu = ({
  triggerElement,
  asChild,
  location,
}: WorkspacePageCreateMenuProps) => {
  const { getPermissions } = usePermissionsContext();
  const flags = useFlags();
  const {
    folderId,
    handleOpenRecorder,
    goToCreateOverlayDemoPage,
    overlayFlag,
    goToCreateOverlayInFolder,
    goToPlaybook,
  } = useWorkspacePageProps();

  const organizationId = useSelector(selectOrganizationId);
  const { workspaceId } = useParams<{
    workspaceId?: string;
  }>();
  const { trackPlaybookClickCreate } = usePlaybookAnalytics(location);
  const { modalActionsByName } = useModalActions();
  const createNewDemoModal = modalActionsByName('create-new-demo-modal');
  const selectBaseForDemo = modalActionsByName('select-base-for-demo-modal');
  const createTour = modalActionsByName('create-tour-modal');
  const createPlaybook = modalActionsByName('create-playbook-modal');

  const { data: demosData } = useGetDemosQuery({
    organizationId,
    workspaces: workspaceId,
  });

  const { data: toursData } = useGetToursQuery({
    organizationId,
    workspaces: workspaceId,
  });
  const demos = demosData?.items;
  const tours = toursData?.items;

  const createRecordingPlanPermission = getPermissions(
    PermissionKey.record,
    PermissionAction.createRecording
  );
  const createADemoPlanPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.createADemo
  );
  const createPlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.createPlaybook
  );

  return (
    <>
      {flags.playbook ? (
        <CreateAssetMenu
          onCreateDemo={() => {
            createADemoPlanPermission
              ? selectBaseForDemo.openModal()
              : goToCreateOverlayDemoPage();
          }}
          onCreateTour={() => createTour.openModal()}
          onCreateClone={() => handleOpenRecorder(workspaceId, folderId)}
          onCreatePlaybook={() => {
            createPlaybook.openModal();
            trackPlaybookClickCreate();
          }}
          triggerElement={triggerElement}
          disabledItems={!tours?.length && !demos?.length ? ['playbook'] : []}
        />
      ) : (
        <Dropdown
          asChild={asChild}
          align="center"
          arrow
          width={136}
          triggerElement={triggerElement}
        >
          <div className="p-1.5">
            <DropdownItem
              onClick={() => {
                createADemoPlanPermission
                  ? selectBaseForDemo.openModal()
                  : goToCreateOverlayDemoPage();
              }}
            >
              <div className="flex p-2.5 gap-2.5 items-center cursor-pointer rounded-md">
                <DemoIndicatorColoredIcon />
                <TextBase
                  label="Demo"
                  color="text-ds-natural-600"
                />
              </div>
            </DropdownItem>
            <DropdownItem onClick={() => createTour.openModal()}>
              <div className="flex p-2.5 items-center gap-2.5 rounded-md cursor-pointer">
                <TourIndicatorColoredIcon />
                <TextBase
                  label="Tour"
                  color="text-ds-natural-600"
                />
              </div>
            </DropdownItem>
            {flags.playbook && createPlaybookPermission && (
              <DropdownItem
                onClick={() => {
                  createPlaybook.openModal();
                  trackPlaybookClickCreate();
                }}
              >
                <div className="flex p-2.5 items-center gap-2.5 rounded-md cursor-pointer">
                  <PlaybookIcon />
                  <TextBase
                    label="Playbook"
                    color="text-ds-natural-600"
                  />
                </div>
              </DropdownItem>
            )}
            {createRecordingPlanPermission && (
              <>
                <div className="my-1">
                  <Separator horizontal />
                </div>
                <DropdownItem
                  onClick={() => handleOpenRecorder(workspaceId, folderId)}
                >
                  <div className="flex p-2.5 items-center gap-2.5 rounded-md cursor-pointer">
                    <RecordingIndicatorColoredIcon />
                    <TextBase
                      label="Clone"
                      color="text-ds-natural-600"
                    />
                  </div>
                </DropdownItem>
              </>
            )}
          </div>
        </Dropdown>
      )}
      {selectBaseForDemo.isModalOpen() && (
        <CreateEntityModal
          open={selectBaseForDemo.isModalOpen()}
          onClose={selectBaseForDemo.closeModal}
          handleContinue={(selected) => {
            if (selected === 'clone') {
              createNewDemoModal.openModal();
            }
            if (selected === 'overlay') {
              if (folderId) {
                goToCreateOverlayInFolder(workspaceId, folderId);
              } else {
                goToCreateOverlayDemoPage();
              }
            }
            selectBaseForDemo.closeModal();
          }}
          base="demo"
          isDemoOverlayDisabled={!overlayFlag}
          isTourFromUrlDisabled={!demos?.length}
        />
      )}
      {createPlaybook.isModalOpen() && (
        <CreatePlaybookModal
          workspaceId={workspaceId}
          onClose={() => createPlaybook.closeModal()}
          onSuccess={goToPlaybook}
        />
      )}
    </>
  );
};
